/* eslint-disable promise/prefer-await-to-then */
import {
	CommandBar,
	DefaultButton,
	DetailsListLayoutMode,
	Dialog,
	DialogFooter,
	DialogType,
	IColumn,
	ICommandBarItemProps,
	IconButton,
	Label,
	MessageBar,
	MessageBarType,
	Panel,
	PanelType,
	Pivot,
	PivotItem,
	PrimaryButton,
	ProgressIndicator,
	SelectionMode,
	ShimmeredDetailsList,
	Stack
} from "@fluentui/react"
import { useEffect, useState } from "react"
import { useApiCall } from "../hooks/apiHooks"
import { addQualifyingEvent, getClassNames, getCurrentUserEmail, getEnrollementRecordsFromHouseholdMembers, safeFormatDateValue, sortHouseHoldMembers } from "../lib/helper"
import { makeCRMApiCall } from "../services/apiService"
import { IAccount } from "../types/IAccount"
import { IEmployee } from "../types/IEmployee"
import { IEnrollmentRecord } from "../types/IEnrollmentRecord"
import { IGroupPlan } from "../types/IGroupPlan"
import { EditHouseholdMemberPanel } from "./editHouseholdMemberPanel"
import { TerminateDialog } from "./terminateMemberDialog"
import { TermMemberDialog } from "./termMemberDialog"

interface ViewHouseholdMemberDialogProps {
	closePanel: (reload: boolean, success: boolean) => void
	employeeHouseholdMember: IEnrollmentRecord
	employeeHouseholdMembers: IEnrollmentRecord[]
	isEmployee: boolean
	employeeId?: string
	groupPlans: IGroupPlan[]
	group: IAccount
}

export function ViewHouseholdMemberDialog({ closePanel, employeeHouseholdMember, employeeHouseholdMembers, isEmployee, employeeId, groupPlans, group }: ViewHouseholdMemberDialogProps) {
	const [removeHouseholdMember, setRemoveHouseholdMember] = useState<IEnrollmentRecord | undefined>(undefined)
	const [showNewMemberPanel, setShowNewMemberPanel] = useState(false)
	const [formEmployee, setFormEmployee] = useState<Partial<IEmployee>>()

	const [editHHM, setEditHHM] = useState<IEnrollmentRecord>()
	const [terminateHHM, setTerminateHHM] = useState<IEnrollmentRecord>()
	const [termHHM, setTermHHM] = useState<IEnrollmentRecord>()
	const [refreshFlag, setRefreshFlag] = useState<number>(0)

	useEffect(() => {
		if (isEmployee && employeeId) {
			makeCRMApiCall(`cw_employees(${employeeId})`)
				.then(employee => {
					setFormEmployee(employee)
				})
				.catch(err => {
					console.error(err)
				})
		}
	}, [isEmployee, employeeId])
	const {
		data: qualifyingEvents,
		loading: loadingQualifyingEvents,
		error: errorQualifyingEvents
	} = useApiCall<any>(
		`/cw_employees?$expand=cw_cw_qualifyingevent_to_employee($select=cw_name,cw_updatedate,cw_username;$orderby=cw_updatedate desc)&$filter=cw_employeeid eq '${employeeHouseholdMember.employee_id}'`,
		{
			crm: true,
			refetchOnChanges: [refreshFlag]
		}
	)

	const {
		data: issuedCoverages,
		loading: loadingIssuedCoverages,
		error: errorIssuedCoverages
	} = useApiCall<any>(
		`/cw_issuedcoverages?$select=cw_coveragetype,_cw_householdmember_value,cw_issueddateeffective,cw_issuedenddate,cw_name,_cw_plan_value,cw_islatest&$expand=cw_HouseholdMember($select=cw_firstname,cw_lastname,cw_relationship),cw_Plan($select=cw_plancode,cw_name)&$filter=_cw_employee_value eq ${employeeHouseholdMember.employee_id}`,
		{
			crm: true,
			refetchOnChanges: [refreshFlag]
		}
	)

	const {
		data: householdMembers,
		fetchData: fetchHouseholdMembers,
		loading: loadingHouseholdMembers,
		error: errorHouseholdMembers,
		setData: setHouseholdMembers
	} = useApiCall<IEnrollmentRecord[]>(`/getHouseholdMembersForEmployee/?employeeid=${employeeHouseholdMember.employee_id}`, {
		crm: true,
		customApiPath: "_acmgapi",
		handlerOnly: true,
		resultParser: (result: any) => {
			let resultJson = result
			if (typeof result === "string") {
				resultJson = JSON.parse(result)
			}
			const householdMembers = resultJson.map(m => ({ ...m, cw_relationship: m.cw_relationship ? Number(m.cw_relationship) : undefined }))
			return getEnrollementRecordsFromHouseholdMembers(sortHouseHoldMembers(householdMembers))
		}
	})

	useEffect(() => {
		setHouseholdMembers(employeeHouseholdMembers)
	}, [])

	useEffect(() => {
		if (refreshFlag > 0) {
			fetchHouseholdMembers()
		}
	}, [refreshFlag])

	const qualifyingEventsColumns: IColumn[] = [
		{
			key: "cw_name",
			name: "Event",
			fieldName: "cw_name",
			minWidth: 700
		},
		{
			key: "cw_username",
			name: "User",
			fieldName: "cw_username",
			minWidth: 180
		},
		{
			key: "cw_updatedate",
			name: "Date",
			fieldName: "cw_updatedate",
			minWidth: 180,
			onRender: (item: any) => {
				return safeFormatDateValue(item.cw_updatedate, "MM/dd/yyyy h:mm:ss a")
			}
		}
	]

	const issuedCoveragesColumns: IColumn[] = [
		{
			key: "cw_name",
			name: "Name",
			fieldName: "cw_name",
			minWidth: 180,
			onRender: (item: any) => {
				return item.cw_name
			}
		},
		{
			key: "cw_islatest",
			name: "Is Latest?",
			fieldName: "cw_islatest",
			minWidth: 70,
			onRender: (item: any) => {
				return item.islatest ? "Yes" : "No"
			}
		},
		{
			key: "cw_issueddateeffective",
			name: "Issued Date Effective",
			fieldName: "cw_issueddateeffective",
			minWidth: 180,
			onRender: (item: any) => {
				return safeFormatDateValue(item.cw_issueddateeffective, "MM/dd/yyyy")
			}
		},
		{
			key: "cw_issuedenddate",
			name: "Issued End Date",
			fieldName: "cw_issuedenddate",
			minWidth: 180,
			onRender: (item: any) => {
				return safeFormatDateValue(item.cw_issuedenddate, "MM/dd/yyyy", "")
			}
		},
		{
			key: "cw_coveragetype",
			name: "Coverage Type",
			fieldName: "cw_coveragetype",
			minWidth: 700,
			onRender: (item: any) => {
				return item["cw_coveragetype@OData.Community.Display.V1.FormattedValue"]
			}
		},
		{
			key: "_cw_plan_value",
			name: "Plan",
			fieldName: "_cw_plan_value",
			minWidth: 100,
			onRender: (item: any) => {
				return item?.cw_Plan?.cw_name || "None"
			}
		}
	]

	const householdMemberColumns: IColumn[] = [
		{
			key: "Edit",
			minWidth: 30,
			name: "",
			isIconOnly: true,
			onRender: (item: IEnrollmentRecord) => {
				return (
					<IconButton
						id="ContextualMenuButton1"
						className={getClassNames().child}
						text=""
						width="30"
						split={false}
						iconProps={{ iconName: "MoreVertical" }}
						menuIconProps={{ iconName: "" }}
						menuProps={{
							shouldFocusOnMount: true,
							items: [
								{
									key: "EditEmployee",
									text: "Edit Household Member",
									iconProps: {
										iconName: "Edit"
									},
									onClick: () => setEditHHM(item)
								},
								{
									key: "Term",
									text: "Term Household Member",
									iconProps: {
										iconName: "Calendar"
									},
									onClick: () => setTermHHM(item)
								}
								// {
								// 	key: "Terminate",
								// 	text: "Terminate Household Member",
								// 	iconProps: {
								// 		iconName: "Trash"
								// 	},
								// 	onClick: () => setTerminateHHM(item)
								// }
							]
						}}
					/>
				)
			}
		},
		{
			key: "firstName",
			name: "First Name",
			fieldName: "firstName",
			minWidth: 100
		},
		// {
		// 	key: "middleName",
		// 	name: "Middle Name",
		// 	fieldName: "middleName",
		// 	minWidth: 100
		// },
		{
			key: "lastName",
			name: "Last Name",
			fieldName: "lastName",
			minWidth: 100
		},
		{
			key: "relationship",
			name: "Relationship",
			fieldName: "relationship",
			minWidth: 100
		},
		{
			key: "gender",
			name: "Gender",
			fieldName: "gender",
			onRender: (item: IEnrollmentRecord) => {
				return Number(item.gender) === 121640000 ? "MALE" : Number(item.gender) === 121640001 ? "FEMALE" : ""
			},
			minWidth: 100
		},
		{
			key: "SSN",
			name: "SSN",
			fieldName: "SSN",
			minWidth: 100
		},
		{
			key: "InsEffDate",
			minWidth: 120,
			name: "Ins Eff Date",
			onRender: (item: IEnrollmentRecord) => {
				return item.insEffDate ? safeFormatDateValue(item.insEffDate, "MM/dd/yyyy") : "N/A"
			}
		},
		{
			key: "InsTermDate",
			minWidth: 120,
			name: "Ins Term Date",
			onRender: (item: IEnrollmentRecord) => {
				return item.insTermDate ? safeFormatDateValue(item.insTermDate, "MM/dd/yyyy") : "N/A"
			}
		},
		{
			key: "birthday",
			minWidth: 150,
			name: "Birthday(mm/dd/yyyy)",
			onRender: (item: IEnrollmentRecord) => {
				return item.birthday ? safeFormatDateValue(item.birthday, "MM/dd/yyyy") : ""
			}
		}
		// {
		// 	key: "ZipCode",
		// 	minWidth: 120,
		// 	name: "Zip Code",
		// 	onRender: (item: IEnrollmentRecord) => {
		// 		return item.cw_hhzip || "N/A"
		// 	}
		// }
	]

	const getCommandBarItems = (): ICommandBarItemProps[] => {
		const items: ICommandBarItemProps[] = [
			{
				key: "newHouseholdMember",
				text: "New Household Member",
				commandBarButtonAs: () => {
					return <PrimaryButton iconProps={{ iconName: "PeopleAdd" }} style={{ marginRight: 10 }} text="New Household Member" onClick={() => setShowNewMemberPanel(true)} />
				}
			}
		]
		return items
	}

	//TODO: maybe make the "Employee Info" page editable
	return (
		<Dialog
			onDismiss={() => closePanel(true, true)}
			hidden={false}
			dialogContentProps={{
				type: DialogType.normal,
				title: (
					<Stack horizontalAlign="center">
						<h3 style={{ marginTop: 0 }}>
							{employeeHouseholdMember.firstName} {employeeHouseholdMember.lastName}
						</h3>
					</Stack>
				)
			}}
			modalProps={{
				isBlocking: true
			}}
			styles={{
				main: {
					width: "calc(100vw - 180px) !important",
					minWidth: "calc(100vw - 180px) !important",
					maxWidth: "calc(100vw - 180px) !important",
					height: "calc(100vh - 100px)"
				}
			}}>
			<Stack tokens={{ childrenGap: 8 }}>
				<Pivot>
					<PivotItem headerText="Employee Info">
						<Stack horizontal tokens={{ childrenGap: 100 }} padding={20}>
							<Stack tokens={{ childrenGap: 24 }}>
								<Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
									<Label>First Name:</Label>
									<span>{employeeHouseholdMember.firstName}</span>
								</Stack>
								<Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
									<Label>Middle Initial:</Label>
									<span>{employeeHouseholdMember.middleName.substring(0, 1)}</span>
								</Stack>
								<Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
									<Label>Last Name:</Label>
									<span>{employeeHouseholdMember.lastName}</span>
								</Stack>
								{/* <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
								<Label>Relationship</Label>
								<span>{employeeHouseholdMember.relationship}</span>
							</Stack> */}
								<Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
									<Label>Address:</Label>
									<span>{formEmployee?.cw_address}</span>
								</Stack>
								<Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
									<Label>Address2:</Label>
									<span>{formEmployee?.cw_addressline2}</span>
								</Stack>
								<Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
									<Label>City:</Label>
									<span>{formEmployee?.cw_city}</span>
								</Stack>
								<Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
									<Label>State:</Label>
									<span>{formEmployee?.cw_state}</span>
								</Stack>
								{/* <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
									<Label>Plan</Label>
									<span>{employeeHouseholdMember.planSelection || "None"}</span>
								</Stack> */}
								<Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
									<Label>Zip Code:</Label>
									<span>{employeeHouseholdMember.cw_hhzip || "None"}</span>
								</Stack>
							</Stack>
							<Stack tokens={{ childrenGap: 24 }}>
								<Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
									<Label>SSN:</Label>
									<span>{formEmployee?.cw_ssn}</span>
								</Stack>
								<Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
									<Label>Preferred Email:</Label>
									<span>{formEmployee?.cw_email}</span>
								</Stack>
								<Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
									<Label>Preferred Phone No:</Label>
									<span>{formEmployee?.cw_phone}</span>
								</Stack>
								<Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
									<Label>Birth Day:</Label>
									<span>{formEmployee?.cw_birthdate ? safeFormatDateValue(formEmployee.cw_birthdate, "MM/dd/yyyy") : "N/A"}</span>
								</Stack>
							</Stack>
						</Stack>
					</PivotItem>
					<PivotItem headerText="Household Members">
						<Stack tokens={{ childrenGap: 8 }}>
							<CommandBar items={getCommandBarItems()} styles={{ root: { marginTop: 12 } }} />
							{errorHouseholdMembers && <MessageBar messageBarType={MessageBarType.error}>{errorHouseholdMembers}</MessageBar>}
							<ShimmeredDetailsList
								enableShimmer={loadingHouseholdMembers}
								selectionMode={SelectionMode.none}
								items={householdMembers}
								columns={householdMemberColumns}
								layoutMode={DetailsListLayoutMode.fixedColumns}
							/>
						</Stack>
					</PivotItem>
					<PivotItem headerText="Issued Coverages">
						{errorIssuedCoverages && <MessageBar messageBarType={MessageBarType.error}>{errorIssuedCoverages}</MessageBar>}
						{!loadingIssuedCoverages && issuedCoverages?.value?.length === 0 ? (
							<div>There are no issued coverages</div>
						) : (
							<ShimmeredDetailsList
								enableShimmer={loadingIssuedCoverages}
								selectionMode={SelectionMode.none}
								items={issuedCoverages?.value}
								columns={issuedCoveragesColumns}
								layoutMode={DetailsListLayoutMode.fixedColumns}
							/>
						)}
					</PivotItem>
					<PivotItem headerText="Record Tracking">
						<Stack tokens={{ childrenGap: 8 }}>
							{errorQualifyingEvents && <MessageBar messageBarType={MessageBarType.error}>{errorQualifyingEvents}</MessageBar>}
							<ShimmeredDetailsList
								enableShimmer={loadingQualifyingEvents}
								selectionMode={SelectionMode.none}
								items={qualifyingEvents?.value?.[0]?.cw_cw_qualifyingevent_to_employee}
								columns={qualifyingEventsColumns}
								layoutMode={DetailsListLayoutMode.fixedColumns}
							/>
						</Stack>
					</PivotItem>
				</Pivot>
			</Stack>
			{removeHouseholdMember && (
				<DeleteHouseholdMemberDialog
					closePanel={(reload, success) => {
						if (reload && success) {
							//setHouseholdMembers(householdMembers.filter(m => m.cw_householdmembersid !== removeHouseholdMember.cw_householdmembersid))
							setRefreshFlag(refreshFlag + 1)
						}
						setRemoveHouseholdMember(undefined)
					}}
					householdMember={removeHouseholdMember}
				/>
			)}
			{showNewMemberPanel && (
				<EditHouseholdMemberPanel
					householdMember={undefined}
					closePanel={(reload, success) => {
						if (reload && success) {
							setShowNewMemberPanel(false)
							setRefreshFlag(refreshFlag + 1)
						} else {
							setShowNewMemberPanel(false)
						}
					}}
					addHouseholdMember
					isEmployee={false}
					employeeId={employeeHouseholdMember.employee_id}
					groupPlans={[]}
					group={group}
				/>
			)}
			{editHHM && (
				<EditHouseholdMemberPanel
					closePanel={(refreshItems: boolean) => {
						if (refreshItems) {
							setRefreshFlag(refreshFlag + 1)
						}
						setEditHHM(undefined)
					}}
					householdMember={editHHM}
					addHouseholdMember={editHHM?.cw_householdmembersid === undefined}
					isEmployee={editHHM.relationship === "Employee"}
					employeeId={editHHM.employee_id}
					groupPlans={groupPlans}
					group={group}
				/>
			)}
			{terminateHHM && (
				<TerminateDialog
					closePanel={(refreshItems: boolean) => {
						if (refreshItems) {
							setRefreshFlag(refreshFlag + 1)
						}
						setTerminateHHM(undefined)
					}}
					householdMember={terminateHHM}
				/>
			)}
			{termHHM && (
				<TermMemberDialog
					closePanel={(refreshItems: boolean) => {
						if (refreshItems) {
							setRefreshFlag(refreshFlag + 1)
						}
						setTermHHM(undefined)
					}}
					employeeHouseholdMember={termHHM}
					householdMembers={termHHM.relationship === "Employee" ? householdMembers : [termHHM]}
				/>
			)}
		</Dialog>
	)
}

interface DeleteHouseholdMemberDialogProps {
	closePanel: (reload: boolean, success: boolean) => void
	householdMember: IEnrollmentRecord
}

export function DeleteHouseholdMemberDialog({ closePanel, householdMember }: DeleteHouseholdMemberDialogProps) {
	const [isTerminating, setIsTerminating] = useState(false)
	const [error, setError] = useState<string | undefined>(undefined)

	return (
		<Dialog
			onDismiss={() => closePanel(false, false)}
			hidden={false}
			dialogContentProps={{
				type: DialogType.normal,
				title: (
					<Stack horizontalAlign="center">
						<h3 style={{ marginTop: 0 }}>Remove Household Member</h3>
					</Stack>
				)
			}}
			modalProps={{
				isBlocking: true
			}}>
			<Stack tokens={{ childrenGap: 8 }}>
				<div style={{ fontSize: 18, fontWeight: 500 }}>Are you sure you want to remove the following household member?</div>
				<Stack tokens={{ childrenGap: 8 }}>
					<Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
						<Label>Name</Label>
						<span>
							{householdMember.firstName} {householdMember.lastName}
						</span>
					</Stack>
				</Stack>
			</Stack>
			<DialogFooter>
				<Stack>
					<Stack horizontalAlign="center" horizontal tokens={{ childrenGap: 12 }}>
						<PrimaryButton
							disabled={isTerminating}
							onClick={async () => {
								setIsTerminating(true)
								try {
									await makeCRMApiCall(`cw_householdmemberses(${householdMember.cw_householdmembersid})`, "DELETE")
									const contactEmail = await getCurrentUserEmail()
									await addQualifyingEvent(contactEmail, `Removed household member for ${householdMember.firstName} ${householdMember.lastName}`, householdMember.employee_id)
									closePanel(true, true)
								} catch (e) {
									console.error(e)
									setError("An error occurred while removing the household member.")
								}
							}}
							text="Remove"
						/>
						<DefaultButton onClick={() => closePanel(false, false)} text="Cancel" />
					</Stack>
					{error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
				</Stack>
			</DialogFooter>
		</Dialog>
	)
}

interface NewHouseholdMemberPanelProps {
	closePanel: (reload: boolean, success: boolean, newMember?: IEnrollmentRecord) => void
	employeeHouseholdMember: IEnrollmentRecord
}

export function NewHouseholdMemberPanel({ closePanel, employeeHouseholdMember }: NewHouseholdMemberPanelProps) {
	const [error, setError] = useState<string | undefined>()
	const [saving, setSaving] = useState(false)

	const saveItem = async () => {
		setSaving(true)
		try {
			//TODO: Implement
		} catch (e) {
			setError("An error occurred while saving the household member.")
			console.error(e)
		} finally {
			setSaving(false)
		}
	}

	return (
		<Panel
			type={PanelType.custom}
			customWidth="700px"
			onDismiss={() => closePanel(false, false)}
			isOpen
			isFooterAtBottom
			headerText="New Household Member"
			onRenderFooterContent={() => (
				<Stack tokens={{ childrenGap: 10 }}>
					{error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
					{saving && <ProgressIndicator label="This may take a minute" />}
					<Stack horizontal tokens={{ childrenGap: 10 }}>
						<PrimaryButton onClick={saveItem} disabled={saving}>
							Save
						</PrimaryButton>
						<DefaultButton onClick={() => closePanel(false, false)}>Cancel</DefaultButton>
					</Stack>
				</Stack>
			)}>
			TBD
		</Panel>
	)
}
